import React, { useState, useEffect } from "react";
import RoleBasedLayout from "./RoleBasedLayout";
import Input from "../ui/Input";
import Button from "../ui/Button";
import ReactQuill from "../ui/RichText";
import SucessToast from "../ui/SucessToast";
import { Pencil, Trash2, Eye, X } from "lucide-react";
import SkeletonPage from "../ui/SkeletonPage ";
import Pagination from "../ui/Pagination";
import { useHoliday, useTotalHolidays, useUpdateHoliday, useDeleteHoliday, useUser } from "../Use-auth";
import DateRangePicker from "../ui/DateRangePicker";
import moment from "moment";

const InfoRow = ({ label, value, className }) => (
    <tr className="text-[10px] sm:text-[12px]">
        <td className="px-2 py-1 sm:px-4 sm:py-2 font-medium text-gray-600 w-1/3">
            {label}
        </td>

        <td className={`px-2 py-1 sm:px-4 sm:py-2 text-gray-800 ${className} inline-block `}>
            {value || "-"}
        </td>
    </tr >
);

export default function HolidaysAndPolicies() {
    const [form, setForm] = useState({
        title: "",
        startDate: null,
        endDate: null,
        description: "",
        type: "holiday",
    });
    const [loading, setLoading] = useState(false);
    const { mutate: createHoliday } = useHoliday();
    const { data: holidaysData, isLoading, isFetching } = useTotalHolidays();
    const holidays = holidaysData?.holidays || [];
    const { mutate: updateHoliday } = useUpdateHoliday();
    const { mutate: deleteHoliday } = useDeleteHoliday();
    const { data: user } = useUser();
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState("");
    const [editingIndex, setEditingIndex] = useState(null);
    const [showPopup, setShowPopup] = useState(false);
    const [previewHoliday, setPreviewHoliday] = useState(null);
    const [search, setSearch] = useState('');
    const isHR = user?.department === "HR" || user?.department === "Admin";
    const [currentPage, setCurrentPage] = useState(1);
    const formsPerPage = 20;
    const [isPickerOpen, setIsPickerOpen] = useState(false);

    // Fetch existing holidays on mount

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleDescriptionChange = (value) => {
        setForm((prev) => ({ ...prev, description: value }));
    };

    const handleHoliday = (e) => {
        e.preventDefault();
        setLoading(true);

        const formattedData = {
            ...form,
            startDate: form.startDate
                ? moment(form.startDate).format("YYYY-MM-DD")
                : null,
            endDate: form.endDate
                ? moment(form.endDate).format("YYYY-MM-DD")
                : null,
        };

        if (editingIndex !== null) {
            const holidayId = holidays[editingIndex].id;

            updateHoliday(
                { id: holidayId, body: formattedData },
                {
                    onSuccess: () => {
                        setEditingIndex(null);

                        setForm({
                            title: "",
                            startDate: null,
                            endDate: null,
                            description: "",
                            type: "holiday",
                        });

                        setLoading(false);
                        setShowToast(true);
                        setTimeout(() => setShowToast(false), 3000);
                    },
                    onError: (error) => {
                        console.error("Error updating holiday:", error);
                        setLoading(false);
                    },
                }
            );
        } else {
            createHoliday(
                formattedData,
                {
                    onSuccess: () => {
                        setToastMessage(
                            form.type === "holiday"
                                ? "Holiday added successfully!"
                                : form.type === "policy"
                                    ? "Policy added successfully!"
                                    : "Optional added successfully!"
                        );

                        setForm({
                            title: "",
                            startDate: null,
                            endDate: null,
                            description: "",
                            type: "holiday",
                        });

                        setLoading(false);
                        setShowPopup(false);
                        setShowToast(true);
                        setTimeout(() => setShowToast(false), 3000);
                    },
                    onError: (error) => {
                        console.error("Error creating holiday:", error);
                        setLoading(false);
                    },
                }
            );
        }
    };

    const handleEdit = (index) => {
        setShowPopup(true);

        const item = holidays[index];

        const formattedDate = item.date
            ? new Date(item.date).toLocaleDateString("en-CA", {
                timeZone: "Asia/Kolkata"
            })
            : "";

        setForm({
            title: item.title,
            startDate: item.startDate ? new Date(item.startDate) : null,
            endDate: item.endDate ? new Date(item.endDate) : null,
            description: item.description,
            type: item.type,
        });

        setEditingIndex(index);
    };

    const handleDelete = (index) => {
        const holiday = holidays[index];

        if (!holiday?.id) {
            console.error("Invalid holiday ID", holiday);
            return;
        }

        const confirmDelete = window.confirm(
            `Are you sure you want to delete "${holiday.title}"?`
        );

        if (!confirmDelete) return;

        deleteHoliday(holiday.id, {
            onSuccess: () => {
                setToastMessage("Holiday deleted successfully!");
                setShowToast(true);
                setTimeout(() => setShowToast(false), 3000);
            },
            onError: (error) => {
                console.error("Error deleting holiday:", error);
            },
        });
    };

    const filteredHolidaysBySearch = holidays
        .filter(h => h.title.toLowerCase().includes(search.toLowerCase()));

    const totalPages = Math.ceil(filteredHolidaysBySearch.length / formsPerPage);
    const currentForms = filteredHolidaysBySearch.slice(
        (currentPage - 1) * formsPerPage,
        currentPage * formsPerPage
    ).reverse();

    useEffect(() => {
        setCurrentPage(1);
    }, [search]);

    const handlePageChange = (page) => setCurrentPage(page);

    const holidaysByMonth = currentForms.reduce((acc, holiday) => {
        const month = moment(holiday.startDate).format("MMMM YYYY");

        if (!acc[month]) {
            acc[month] = [];
        }

        acc[month].push(holiday);

        return acc;
    }, {});

    const sortedMonths = Object.entries(holidaysByMonth).sort(
        ([a], [b]) => moment(a, "MMMM YYYY").toDate() - moment(b, "MMMM YYYY").toDate()
    );

    if (isLoading && !holidaysData) {
        return (
            <RoleBasedLayout>
                <SkeletonPage />
            </RoleBasedLayout>
        );
    }

    return (
        <RoleBasedLayout >
            <div className="relative  bg-gray-50">
                <div className="relative z-1 h-full overflow-y-auto p-2 sm:p-6  space-y-8">
                    <div className="flex justify-end w-full mb-2 sm:mb-4">
                        <Input
                            type="search"
                            placeholder="Search"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="w-full sm:w-64"
                        />
                    </div>

                    {isHR && (<div>
                        <Button
                            onClick={() => setShowPopup(true)}
                        >
                            Add New Holiday
                        </Button>
                        {showPopup && (
                            <div>
                                <h3 className="my-3 text-[12px] sm:text-lg font-semibold text-gray-800">Holidays & Policies</h3>
                                <form
                                    onSubmit={handleHoliday}
                                    className="bg-white rounded-xl shadow-md p-2 sm:p-6 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-5"
                                >
                                    {/* Title */}
                                    <Input
                                        name="title"
                                        placeholder="Holiday Title"
                                        value={form.title}
                                        onChange={handleChange}
                                        required
                                        className="sm:col-span-2 md:col-span-2 border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#fbe5e9] transition duration-200"
                                    />

                                    {/* Date */}
                                    <div className="relative">
                                        <Input
                                            type="text"
                                            readOnly
                                            value={
                                                form.startDate && form.endDate
                                                    ? `${moment(form.startDate).format("DD-MM-YYYY")} - ${moment(form.endDate).format("DD-MM-YYYY")}`
                                                    : form.startDate
                                                        ? moment(form.startDate).format("DD-MM-YYYY")
                                                        : ""
                                            }
                                            placeholder="Select Date Range"
                                            onClick={() => setIsPickerOpen(true)}
                                            required
                                            className="border border-gray-300 rounded-lg px-3 py-2 w-full cursor-pointer"
                                        />

                                        <DateRangePicker
                                            open={isPickerOpen}
                                            selectedRange={form.startDate && form.endDate ? form : null}
                                            onApply={({ startDate, endDate }) => {
                                                setForm((prev) => ({
                                                    ...prev,
                                                    startDate,
                                                    endDate: endDate || startDate,
                                                }));
                                                setIsPickerOpen(false);
                                            }}
                                            onClose={() => setIsPickerOpen(false)}
                                        />
                                    </div>
                                    {/* Type */}
                                    <select
                                        name="type"
                                        value={form.type}
                                        onChange={handleChange}
                                        className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#fbe5e9] transition duration-200"
                                    >
                                        <option value="holiday">Holiday</option>
                                        <option value="policy">Policy</option>
                                        <option value="optional">Optional</option>
                                    </select>

                                    <div className="col-span-1 sm:col-span-2 md:col-span-3">
                                        <ReactQuill
                                            theme="snow"
                                            value={form.description}
                                            onChange={handleDescriptionChange}
                                            placeholder="Description (optional)"
                                            className="h-32 border border-gray-300 rounded-lg"
                                        />
                                    </div>

                                    <div className="col-span-1 sm:col-span-2 md:col-span-3 flex flex-col sm:flex-row justify-end gap-3">
                                        <Button
                                            type="button"
                                            onClick={() => {
                                                setShowPopup(false);
                                                setEditingIndex(null);
                                                setForm({ title: "", date: "", description: "", type: "holiday" });
                                            }}
                                            variant="danger"
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            type="submit"
                                            disabled={loading}
                                            variant="success"
                                            className="flex items-center justify-center gap-2 min-w-[110px]"
                                        >
                                            {loading ? (
                                                <>
                                                    Saving...
                                                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                                                </>
                                            ) : editingIndex !== null ? (
                                                "Update"
                                            ) : (
                                                "Save"
                                            )}
                                        </Button>
                                    </div>
                                </form>

                            </div>)}
                    </div>)}
                    <div className="bg-white rounded-lg sm:rounded-xl shadow-md p-1 sm:p-6">
                        <h3 className="text-[14px] sm:text-[16px] md:text-[20px] font-semibold mb-4 text-gray-800">Holiday List</h3>
                        {holidays.length === 0 ? (
                            <p className="text-gray-500 text-sm">No holidays added yet.</p>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="min-w-full border-collapse bg-gray-50 text-left text-[11px] sm:text-sm text-gray-700">
                                    <thead className="bg-gray-100 text-gray-800">
                                        <tr>
                                            <th className="px-2 py-1 sm:px-4 sm:py-2 font-medium border-b">Title</th>
                                            <th className="px-2 py-1 sm:px-4 sm:py-2 font-medium border-b">Date</th>
                                            <th className="px-2 py-1 sm:px-4 sm:py-2 font-medium border-b hidden custom:table-cell">Description</th>
                                            <th className="px-2 py-1 sm:px-4 sm:py-2 font-medium border-b hidden xs:table-cell">Type</th>
                                            <th className="px-2 py-1 sm:px-4 sm:py-2 font-medium border-b">Actions</th>
                                        </tr>
                                    </thead>

                                    <tbody className="divide-y divide-gray-200">
                                        {sortedMonths.map(([month, holidays]) => (
                                            <React.Fragment key={month}>
                                                <tr className="bg-gray-200">
                                                    <td colSpan="5" className="px-4 py-2 font-semibold text-gray-700">
                                                        {month}
                                                    </td>
                                                </tr>
                                                {holidays.map((item) => (
                                                    <tr key={item.id} className="bg-white hover:bg-gray-50 transition">
                                                        <td className="px-2 py-1 sm:px-4 sm:py-2 text-[11px] sm:text-sm text-gray-800">{item.title}</td>
                                                        <td>
                                                            {`${moment(item.startDate).format("DD MMM YYYY")} - ${moment(item.endDate).format("DD MMM YYYY")}`}
                                                        </td>                                                <td className="px-2 py-1 sm:px-4 sm:py-2 text-[11px] sm:text-sm text-gray-600 max-w-xs hidden custom:table-cell">
                                                            {item.description ? (
                                                                (() => {
                                                                    const text = item.description.replace(/<[^>]+>/g, "");
                                                                    return (
                                                                        <span title={text}>
                                                                            {text.length > 50 ? text.slice(0, 50) + "..." : text}
                                                                        </span>
                                                                    );
                                                                })()
                                                            ) : (
                                                                "-"
                                                            )}
                                                        </td>
                                                        <td className="px-2 py-1 sm:px-4 sm:py-2 hidden xs:table-cell">
                                                            <span
                                                                className={`text-xs px-3 py-1 rounded-lg text-[11px] sm:text-sm uppercase ${item.type === "holiday"
                                                                    ? "bg-orange-100 text-orange-700"
                                                                    : item.type === "policy"
                                                                        ? "bg-blue-100 text-blue-700"
                                                                        : item.type === "optional"
                                                                            ? "bg-purple-100 text-purple-700"
                                                                            : "bg-gray-100 text-gray-700"
                                                                    }`}
                                                            >
                                                                {item.type}
                                                            </span>
                                                        </td>

                                                        <td className="px-2 py-1 sm:px-4 sm:py-2 flex gap-0 sm:gap-[5px] text-[11px] sm:text-sm">
                                                            {isHR && (<>
                                                                <span
                                                                    className="p-1 rounded cursor-pointer hover:bg-blue-100 transition"
                                                                    onClick={() =>
                                                                        handleEdit(holidays.findIndex((h) => h.id === item.id))
                                                                    }
                                                                >
                                                                    <Pencil size={14} className="text-blue-500" />
                                                                </span>

                                                                <span
                                                                    className="p-1 rounded cursor-pointer hover:bg-red-100 transition"
                                                                    onClick={() =>
                                                                        handleDelete(holidays.findIndex((h) => h.id === item.id))
                                                                    }
                                                                >
                                                                    <Trash2 size={14} className="text-red-500" />
                                                                </span>
                                                            </>
                                                            )}
                                                            <span className="p-1 rounded cursor-pointer hover:bg-orange-100 transition"
                                                                onClick={() => setPreviewHoliday(item)}>
                                                                <Eye size={14} className="text-orange-500" />
                                                            </span>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </React.Fragment>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        handlePageChange={handlePageChange}
                    />
                </div>
                {showToast && <SucessToast message={toastMessage} />}
                {previewHoliday && (
                    <div
                        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-2 sm:px-4"
                        onClick={() => setPreviewHoliday(null)}
                    >
                        <div
                            className="bg-white relative rounded-lg w-full max-w-[700px] shadow-lg h-[80vh] overflow-hidden animate-scaleIn"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Header */}
                            <div className="sticky top-0 z-10 flex justify-between items-center bg-gray-900 px-4 py-3">
                                <h2 className="text-white font-semibold text-[14px] sm:text-[16px]">Holiday Details</h2>
                                <button
                                    onClick={() => setPreviewHoliday(null)}
                                    className="text-white hover:text-red-400"
                                >
                                    <X size={20} />
                                </button>
                            </div>

                            <div className="p-2 sm:p-6 h-full pb-[60px] space-y-4">

                                <table className="w-full border border-gray-200 rounded-md mb-6">
                                    <tbody className="divide-y divide-gray-200">
                                        {/* Title */}
                                        <InfoRow label="Title" value={previewHoliday.title || "-"} />

                                        {/* Date */}
                                        <InfoRow
                                            label="Date"
                                            value={
                                                previewHoliday.startDate && previewHoliday.endDate
                                                    ? `${moment(previewHoliday.startDate).format("DD MMM YYYY")} - ${moment(previewHoliday.endDate).format("DD MMM YYYY")}`
                                                    : "-"
                                            }
                                        />

                                        {/* Type with colored badge */}
                                        <InfoRow
                                            label="Type"
                                            value={
                                                <span
                                                    className={`inline-block px-3 py-1 rounded-full text-xs sm:text-sm font-semibold capitalize ${previewHoliday.type === "holiday"
                                                        ? "bg-orange-100 text-orange-700"
                                                        : previewHoliday.type === "policy"
                                                            ? "bg-blue-100 text-blue-700"
                                                            : previewHoliday.type === "optional"
                                                                ? "bg-purple-100 text-purple-700"
                                                                : "bg-gray-100 text-gray-700"
                                                        }`}
                                                >
                                                    {previewHoliday.type || "-"}
                                                </span>
                                            }
                                        />

                                        <InfoRow
                                            label="Description"
                                            value={previewHoliday.description ? (
                                                <div
                                                    className="text-gray-700 break-words"
                                                    dangerouslySetInnerHTML={{ __html: previewHoliday.description }}
                                                />
                                            ) : (
                                                "-"
                                            )}
                                        />
                                    </tbody>
                                </table>


                            </div>
                        </div>
                    </div>
                )}
            </div>
        </RoleBasedLayout >
    );
}
